import { useMemo, useState } from 'react';
import { apiFetch } from '@/lib/api.js';
import { useParams, useNavigate } from 'react-router-dom';
import { useEvents } from '../lib/useEvents.js';
import { StatusDot, groupByDate, DriftDot, DriftLegend, WEEKDAY_TEMPLATE_MAP } from '../lib/events.jsx';
import { LoadingState, ErrorState } from '../lib/Spinner.jsx';
import { formatClockShort } from '../lib/timeFields.jsx';
import { venueTz, todayKeyInTz, todayPartsInTz } from '../lib/dateKeys.js';

// Compact time chip in the venue's timezone (12h, no zone suffix to fit the
// cell) — consistent with the rest of the app's time display.
function timeChip(startAt, tz) {
  if (!startAt) return null;
  return formatClockShort(startAt, tz);
}

// Month grid. Week starts Sunday. Dates rendered in UTC to match Postgres
// `date` column (no timezone shenanigans).

const DOW_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getMonthGrid(year, month) {
  const first = new Date(Date.UTC(year, month, 1));
  const startDow = first.getUTCDay();
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const prevMonthLast = new Date(Date.UTC(year, month, 0)).getUTCDate();

  const cells = [];
  for (let i = startDow - 1; i >= 0; i--) {
    cells.push({ y: month === 0 ? year - 1 : year, m: month === 0 ? 11 : month - 1, d: prevMonthLast - i, inMonth: false });
  }
  for (let d = 1; d <= last; d++) {
    cells.push({ y: year, m: month, d, inMonth: true });
  }
  while (cells.length % 7 !== 0 || cells.length < 35) {
    const lastCell = cells[cells.length - 1];
    const next = new Date(Date.UTC(lastCell.y, lastCell.m, lastCell.d + 1));
    cells.push({ y: next.getUTCFullYear(), m: next.getUTCMonth(), d: next.getUTCDate(), inMonth: false });
    if (cells.length >= 42) break;
  }
  return cells;
}

function cellKey({ y, m, d }) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export default function EventsCalendar() {
  const { tenantSlug } = useParams();
  const navigate = useNavigate();
  // Include past events — the month grid navigates backwards, so past months
  // need their events too.
  const { events, loading, error, refresh } = useEvents(tenantSlug, { includePast: true });

  const [selected, setSelected] = useState(new Set());
  const [anchorId, setAnchorId] = useState(null);
  const [bulking, setBulking] = useState(false);
  const [bulkError, setBulkError] = useState(null);

  // Visit order = events sorted by (date, talent) — matches what useEvents
  // already returns, so a shift-range collapses to a slice on the events array.
  const eventIndex = useMemo(() => {
    const m = new Map();
    events.forEach((e, i) => m.set(e.id, i));
    return m;
  }, [events]);

  const handleEventClick = (e, ev) => {
    if (ev.shiftKey && anchorId != null && eventIndex.has(anchorId)) {
      ev.preventDefault();
      const a = eventIndex.get(anchorId);
      const b = eventIndex.get(e.id);
      const [lo, hi] = a < b ? [a, b] : [b, a];
      const range = events.slice(lo, hi + 1).map((x) => x.id);
      setSelected((s) => {
        const n = new Set(s);
        range.forEach((id) => n.add(id));
        return n;
      });
      return;
    }
    if (ev.metaKey || ev.ctrlKey) {
      ev.preventDefault();
      setSelected((s) => {
        const n = new Set(s);
        if (n.has(e.id)) n.delete(e.id); else n.add(e.id);
        return n;
      });
      setAnchorId(e.id);
      return;
    }
    if (selected.size > 0) {
      ev.preventDefault();
      setSelected((s) => {
        const n = new Set(s);
        if (n.has(e.id)) n.delete(e.id); else n.add(e.id);
        return n;
      });
      setAnchorId(e.id);
      return;
    }
    navigate(`/${tenantSlug}/event/${e.id}`);
  };

  const runBulk = async (path, body) => {
    setBulking(true);
    setBulkError(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/events/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      await refresh();
      setSelected(new Set());
      setAnchorId(null);
    } catch (e) {
      setBulkError(e.message);
    } finally {
      setBulking(false);
    }
  };

  const [createBusy, setCreateBusy] = useState(null);  // date string currently creating
  const [createErr, setCreateErr] = useState(null);
  const createPlaceholder = async (cell) => {
    const date = `${cell.y}-${String(cell.m + 1).padStart(2, '0')}-${String(cell.d).padStart(2, '0')}`;
    const templateName = WEEKDAY_TEMPLATE_MAP[new Date(Date.UTC(cell.y, cell.m, cell.d)).getUTCDay()]
      || 'Special Event Template';
    setCreateBusy(date); setCreateErr(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/events/create-placeholder`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, template_name: templateName }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || d.detail || 'create_failed');
      await refresh();
    } catch (e) {
      setCreateErr(`${date}: ${e.message}`);
    } finally {
      setCreateBusy(null);
    }
  };

  // Always open on the current month (Today), computed in venue time. The grid
  // navigates to past/future months on demand; deriving the initial month from
  // events[0] would open on the earliest event (e.g. Oct 2023 once past events
  // load) and make the "Today" button (setView(initial)) jump there too.
  const initial = useMemo(() => {
    const tp = todayPartsInTz(venueTz(events));
    return { y: tp.y, m: tp.m };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [view, setView] = useState(initial);

  const byDate = useMemo(() => {
    const m = groupByDate(events);
    // Sort each cell's events by start_at so the headliner-vs-opener
    // ordering is consistent and visible at a glance.
    for (const [k, list] of m.entries()) {
      list.sort((a, b) => (a.start_at || '').localeCompare(b.start_at || ''));
      m.set(k, list);
    }
    return m;
  }, [events]);
  const grid = useMemo(() => getMonthGrid(view.y, view.m), [view]);
  // "Today" in venue time so it matches the List view (which also uses venue tz).
  const tz = venueTz(events);
  const todayKey = todayKeyInTz(tz);
  const tParts = todayPartsInTz(tz);
  const todayDate = new Date(Date.UTC(tParts.y, tParts.m, tParts.d));

  // Year range for the jump picker — span the earliest/latest event years and
  // always include the current view year.
  const years = useMemo(() => {
    let min = view.y, max = view.y;
    for (const e of events) {
      const y = Number(String(e.date).slice(0, 4));
      if (y) { if (y < min) min = y; if (y > max) max = y; }
    }
    const arr = [];
    for (let y = min; y <= max; y++) arr.push(y);
    return arr;
  }, [events, view.y]);
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Which day's events show in the detail panel below the grid (used on both
  // mobile and desktop now). Derived so it auto-resets when the month changes
  // (a selection from the old month won't be an in-month cell of the new grid →
  // fall back to today / first event / 1st).
  const monthDefaultDay = useMemo(() => {
    if (grid.some((c) => c.inMonth && cellKey(c) === todayKey)) return todayKey;
    const firstEv = grid.find((c) => c.inMonth && (byDate.get(cellKey(c)) || []).length);
    if (firstEv) return cellKey(firstEv);
    const firstOfMonth = grid.find((c) => c.inMonth);
    return firstOfMonth ? cellKey(firstOfMonth) : null;
  }, [grid, byDate, todayKey]);
  const [selectedDayRaw, setSelectedDay] = useState(null);
  const selectedDay = (selectedDayRaw && grid.some((c) => c.inMonth && cellKey(c) === selectedDayRaw))
    ? selectedDayRaw : monthDefaultDay;

  function nudge(delta) {
    setView((v) => {
      const m = v.m + delta;
      return { y: v.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 };
    });
  }

  if (loading) return <LoadingState label="Loading calendar…" />;
  if (error) return <ErrorState error={error} onRetry={refresh} />;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h1 className="text-2xl font-semibold">Calendar</h1>
        <div className="flex items-center gap-1.5">
          <button
            className="px-2.5 py-1 text-sm border border-input rounded-md hover:bg-accent whitespace-nowrap"
            onClick={() => nudge(-1)}
            aria-label="Previous month"
          >‹</button>
          <select
            value={view.m}
            onChange={(e) => setView((v) => ({ ...v, m: Number(e.target.value) }))}
            aria-label="Month"
            className="px-2 py-1 text-sm border border-input rounded-md bg-background"
          >
            {MONTHS.map((mo, i) => <option key={i} value={i}>{mo}</option>)}
          </select>
          <select
            value={view.y}
            onChange={(e) => setView((v) => ({ ...v, y: Number(e.target.value) }))}
            aria-label="Year"
            className="px-2 py-1 text-sm border border-input rounded-md bg-background"
          >
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button
            className="px-2.5 py-1 text-sm border border-input rounded-md hover:bg-accent whitespace-nowrap"
            onClick={() => nudge(1)}
            aria-label="Next month"
          >›</button>
          <button
            className="px-2.5 py-1 text-sm border border-input rounded-md hover:bg-accent ml-1 whitespace-nowrap"
            onClick={() => setView(initial)}
          >Today</button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-2 mb-2 p-2 bg-accent rounded-md text-sm">
          <span className="font-medium">{selected.size} selected</span>
          {bulkError && <span className="text-destructive">{bulkError}</span>}
          <div className="flex-1" />
          <select
            disabled={bulking}
            onChange={(e) => {
              if (!e.target.value) return;
              runBulk('bulk-update', { ids: [...selected], updates: { status: e.target.value } });
              e.target.value = '';
            }}
            className="px-2 py-1 text-xs border border-input rounded-md bg-background"
          >
            <option value="">Set status…</option>
            <option value="draft">Draft</option>
            <option value="sized_up">Sized up</option>
            <option value="pending_announce">Pending announce</option>
            <option value="announced">Announced</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button
            disabled={bulking}
            onClick={() => { if (confirm(`Move ${selected.size} event${selected.size === 1 ? '' : 's'} to Recently Deleted? They stay recoverable for 30 days.`)) runBulk('bulk-delete', { ids: [...selected] }); }}
            className="px-2 py-1 text-xs border border-destructive text-destructive rounded-md hover:bg-destructive/10"
          >Delete</button>
          <button
            onClick={() => { setSelected(new Set()); setAnchorId(null); }}
            className="px-2 py-1 text-xs border border-input rounded-md hover:bg-background"
          >Clear</button>
        </div>
      )}
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <div className="hidden md:block text-[11px] text-muted-foreground">
          Shift-click to select a range · ⌘/Ctrl-click to toggle · click an empty future day to add a placeholder
        </div>
        <DriftLegend />
      </div>

      {/* Mobile: real month grid (date + status dots). Tap a day → the shared
          detail panel below lists that day's events. Desktop grid is md+ only. */}
      <div className="md:hidden">
        <div className="grid grid-cols-7 gap-1">
          {DOW_HEADERS.map((h) => (
            <div key={h} className="text-center text-[10px] font-medium text-muted-foreground pb-0.5">{h[0]}</div>
          ))}
          {grid.map((cell, i) => {
            const key = cellKey(cell);
            const dayEvents = byDate.get(key) || [];
            const isToday = key === todayKey;
            const isSel = key === selectedDay;
            const isPast = cell.inMonth && key < todayKey;
            return (
              <button
                key={i}
                onClick={() => setSelectedDay(key)}
                className={`aspect-square flex flex-col items-center justify-start pt-1 rounded-md text-xs transition-colors ${cell.inMonth ? '' : 'opacity-30'} ${isSel ? 'bg-primary/15 ring-1 ring-primary' : isToday ? 'ring-1 ring-primary/40' : 'hover:bg-accent/40'}`}
              >
                <span className={isToday ? 'font-bold text-primary' : isPast ? 'text-muted-foreground' : ''}>{cell.d}</span>
                <div className={`flex flex-wrap gap-0.5 justify-center mt-0.5 px-0.5 ${isPast ? 'opacity-60' : ''}`}>
                  {dayEvents.slice(0, 3).map((e) => (
                    <StatusDot key={e.id} status={e.status} past={isPast} className="flex-shrink-0" />
                  ))}
                  {dayEvents.length > 3 && <span className="text-[8px] text-muted-foreground leading-none">+{dayEvents.length - 3}</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="hidden md:grid grid-cols-7 gap-px bg-border border border-border rounded-md overflow-hidden">
        {DOW_HEADERS.map((h) => (
          <div key={h} className="bg-muted/50 px-2 py-1 text-xs font-medium text-muted-foreground text-center">{h}</div>
        ))}
        {grid.map((cell, i) => {
          const key = cellKey(cell);
          const dayEvents = byDate.get(key) || [];
          const isToday = key === todayKey;
          const isSelDay = key === selectedDay;
          const cellDate = new Date(Date.UTC(cell.y, cell.m, cell.d));
          const isFuture = cellDate >= todayDate;
          const isPast = cell.inMonth && key < todayKey;
          const showCreate = cell.inMonth && isFuture && dayEvents.length === 0;
          const creating = createBusy === key;
          return (
            <div
              key={i}
              onClick={() => setSelectedDay(key)}
              className={[
                'min-h-[110px] p-1.5 text-xs relative group cursor-pointer',
                isSelDay && !isToday ? 'bg-primary/5' : 'bg-background',
                cell.inMonth ? '' : 'opacity-40',
                isToday ? 'ring-2 ring-primary ring-inset' : '',
              ].join(' ')}
            >
              <div className="flex items-center justify-between mb-1">
                <div className={`font-medium ${isToday ? 'text-primary' : isPast ? 'text-muted-foreground' : ''}`}>{cell.d}</div>
                {dayEvents.length > 0 && (
                  <div className="text-[10px] text-muted-foreground">{dayEvents.length}</div>
                )}
              </div>
              <div className={`space-y-0.5 ${isPast ? 'opacity-60' : ''}`}>
                {dayEvents.slice(0, 3).map((e) => {
                  const isSel = selected.has(e.id);
                  const t = timeChip(e.start_at, e.venue_timezone);
                  const onActivate = (ev) => { ev.stopPropagation(); handleEventClick(e, ev); };
                  return (
                    <div
                      key={e.id}
                      title={e.talent}
                      role="button"
                      tabIndex={0}
                      className={`flex items-center gap-1 truncate cursor-pointer hover:text-primary px-1 rounded ${isSel ? 'bg-primary/15 ring-1 ring-primary' : ''} ${isPast ? 'text-muted-foreground' : ''}`}
                      onClick={onActivate}
                      onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') onActivate(ev); }}
                    >
                      <StatusDot status={e.status} past={isPast} className="flex-shrink-0" />
                      {t && <span className="text-muted-foreground text-[10px] tabular-nums">{t}</span>}
                      <span className="truncate flex-1">{e.talent}</span>
                      <DriftDot event={e} size={6} />
                    </div>
                  );
                })}
                {dayEvents.length > 3 && (
                  <button
                    onClick={(ev) => { ev.stopPropagation(); setSelectedDay(key); }}
                    className="text-[10px] text-muted-foreground hover:text-primary"
                  >+{dayEvents.length - 3} more</button>
                )}
              </div>
              {showCreate && (() => {
                const tmpl = WEEKDAY_TEMPLATE_MAP[cellDate.getUTCDay()] || 'Special Event';
                const failed = createErr && createErr.startsWith(key);
                return (
                  <button
                    onClick={(ev) => { ev.stopPropagation(); createPlaceholder(cell); }}
                    disabled={creating}
                    className={`absolute inset-x-1.5 bottom-1.5 text-[10px] py-0.5 px-1 border border-dashed rounded truncate transition-colors disabled:opacity-50 ${failed ? 'border-destructive text-destructive' : 'border-input text-muted-foreground/70 hover:bg-accent hover:text-foreground opacity-70 group-hover:opacity-100'}`}
                    title={failed ? createErr : `Create placeholder using ${tmpl}`}
                  >
                    {creating ? 'Creating…' : failed ? '⚠ failed, retry' : `+ ${tmpl}`}
                  </button>
                );
              })()}
            </div>
          );
        })}
      </div>

      {/* Shared day-detail panel, lists ALL events for the selected day (so days
          with >3 events are reachable on desktop too). Shown on both breakpoints. */}
      {(() => {
        const sel = grid.find((c) => cellKey(c) === selectedDay);
        if (!sel) return null;
        const dayEvents = byDate.get(selectedDay) || [];
        const selDate = new Date(Date.UTC(sel.y, sel.m, sel.d));
        const lbl = selDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', timeZone: 'UTC' });
        const isFuture = selDate >= todayDate;
        const creating = createBusy === selectedDay;
        return (
          <div className="mt-4">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">{lbl}{selectedDay === todayKey ? ' · Today' : ''}{dayEvents.length > 0 ? ` · ${dayEvents.length} event${dayEvents.length === 1 ? '' : 's'}` : ''}</div>
            {dayEvents.length === 0 ? (
              <div className="border border-border rounded-md py-5 text-center text-sm text-muted-foreground">
                No events
                {sel.inMonth && isFuture && (
                  <div className="mt-2">
                    <button
                      onClick={() => createPlaceholder(sel)}
                      disabled={creating}
                      className="px-3 py-1.5 text-xs border border-dashed border-input rounded-md hover:bg-accent hover:text-foreground disabled:opacity-50"
                    >{creating ? 'Creating…' : `+ Add ${WEEKDAY_TEMPLATE_MAP[selDate.getUTCDay()] || 'placeholder'}`}</button>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-md border border-border divide-y divide-border overflow-hidden">
                {dayEvents.map((e) => {
                  const t = timeChip(e.start_at, e.venue_timezone);
                  const isSelE = selected.has(e.id);
                  const onActivate = (ev) => handleEventClick(e, ev);
                  return (
                    <div
                      key={e.id}
                      role="button"
                      tabIndex={0}
                      onClick={onActivate}
                      onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onActivate(ev); } }}
                      className={`flex items-center gap-2.5 px-3 py-2.5 hover:bg-accent/40 active:bg-accent/40 cursor-pointer ${isSelE ? 'bg-primary/10' : 'bg-card'}`}
                    >
                      <StatusDot status={e.status} className="flex-shrink-0" />
                      <span className="font-medium truncate flex-1">{e.talent}</span>
                      <DriftDot event={e} size={6} />
                      {t && <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">{t}</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
